import React from "react";
import {Badge, Button, Checkbox, Drawer, Input, InputNumber, Select} from "antd";
import {FilterOutlined} from "@ant-design/icons";

// The portal's search filters (backend service/search/filters.js) on the flats list, laid out as the portal's search
// panel (agent features/apartment-search/ui/SearchPanel.jsx; the owner, 27.10: «все фильтры аналогично основному
// порталу»): the compact rows always on screen, every other filter behind one button, «Все фильтры», on the side (the
// owner, 30.10: in the admin any filter is a staff one). The same keys, values and rights as in the portal
// (/api/search/config gives the ones this user may use). A value is kept as the portal keeps it: a list of options,
// { from, to }, true / false, or text.

// the portal's compact rows (and in the main filters: «Этаж», the owner 23.10; «Реклама» and «Чей эксклюзив», the owner 30.10)
const COMPACT = ['complex', 'builder', 'rooms', 'deadline', 'finishing', 'district', 'price', 'priceM2', 'area', 'metro', 'distance', 'floor', 'ads', 'exclusiveAgent']
// the list's own status select is on the toolbar; the ids are in the search field; the favorites are the portal's
const SKIP = ['status', 'ids', 'favorites']
// on the table's toolbar, by «Архив»: «Запас» (ECI old's own filter button, the owner 02.11)
const TOOLBAR = ['stock']

const LIST_KINDS = ['multi', 'arrayAny', 'arrayAll']
const RANGE_KINDS = ['range', 'dateRange']
const filled = (v) => v !== undefined && v !== null && v !== ''

export const isSet = (kind, value) => {
  if (!filled(value)) return false
  if (LIST_KINDS.includes(kind)) return value.length > 0
  if (RANGE_KINDS.includes(kind)) return filled(value.from) || filled(value.to)
  return true
}

export const kindsOf = (config) => Object.fromEntries(((config && config.filters) || []).map((f) => [f.key, f.kind]))

// values -> URL parameters as the portal sends them: ?rooms=a&rooms=b&priceFrom=1&top=1&trend=0
export const toParams = (values, kinds) => {
  const params = {}
  Object.entries(values).forEach(([key, value]) => {
    const kind = kinds[key]
    if (!kind || !isSet(kind, value)) return
    if (RANGE_KINDS.includes(kind)) {
      if (filled(value.from)) params[`${key}From`] = value.from
      if (filled(value.to)) params[`${key}To`] = value.to
    } else if (kind === 'flag') params[key] = 1
    else if (kind === 'bool3') params[key] = value ? 1 : 0
    else params[key] = value
  })
  return params
}

// what «Все фильтры» holds: everything but the compact rows
const panelFilters = (config) => ((config && config.filters) || []).filter((f) => !SKIP.includes(f.key) && !COMPACT.includes(f.key) && !TOOLBAR.includes(f.key))

// how many filters are chosen: where — 'panel' («Все фильтры») or 'all'
export const countSet = (values, config, where = 'all') => {
  const kinds = kindsOf(config)
  const inPanel = panelFilters(config).map((f) => f.key)
  return Object.entries(values).filter(([key, value]) => isSet(kinds[key], value) && (where !== 'panel' || inPanel.includes(key))).length
}

const thousands = (v) => (filled(v) ? String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : '')
const digits = (v) => String(v || '').replace(/[^\d.,-]/g, '').replace(',', '.')

// one-of filters: their options as the portal names them; hidden flats are in this list anyway, so only «Только»
const CHOICES = {
  hidden: [['only', 'Только скрытые']],
  apartments: [['only', 'Только апартаменты'], ['exclude', 'Без апартаментов']],
  adsPreset: [['min', 'Минимум'], ['premium', 'Премиум'], ['top', 'Топ']],
}
const choiceLabel = (key, value) => (key === 'reward' ? `от ${thousands(Number(value) / 1000)} тыс. ₽` : value)

const PLACEHOLDERS = {
  floor: '3-7, 9',
  queue: '1, 2',
  build: '1.1',
  houseNumber: '12, 12.1',
  installmentFrom: 'ММ.ГГГГ',
}
// the portal's names in its panel
const NAMES = {rooms: 'Комнаты', price: 'Цена', priceM2: 'Цена за м²', area: 'Площадь', houseNumber: '№ кв.', installmentFrom: 'Платёж с', ads: 'Реклама'}
const nameOf = (filter) => NAMES[filter.key] || filter.label.replace(/,\s*(₽|м²)$/, '')

// a filter's options: its own list, else ECI old's list of the card (the owner 02.11, «Реклама»: as on ECI old, its
// 9 names in its order, not every name the flats and the archive have), else what the flats have (/api/search/facets);
// districts by zone, stations by line
const GROUP_FIELD = {district: 'zone', metro: 'line'}
const OLD_LISTS = {ads: 'ads'}
const optionsOf = (filter, facets, lists) => {
  if (filter.options) return filter.options.map((o) => ({value: o.value, label: o.label}))
  const oldList = lists && OLD_LISTS[filter.key] && lists[OLD_LISTS[filter.key]]
  if (oldList && oldList.length) return oldList.map((o) => ({value: o.value, label: o.label}))
  if (filter.facet) {
    const items = facets[filter.key] || []
    const field = GROUP_FIELD[filter.key]
    if (field && items.some((o) => o[field])) {
      const groups = []
      items.forEach((o) => {
        const label = o[field] || 'Другое'
        let group = groups.find((g) => g.label === label)
        if (!group) groups.push(group = {label, options: []})
        group.options.push({value: o.value, label: String(o.value)})
      })
      return groups
    }
    return items.map((o) => ({value: o.value, label: String(o.value)}))
  }
  return (filter.values || []).map((v) => ({value: v, label: v}))
}

// «от» | «до» in one field with the name in front, as the portal's pill
const Range = ({filter, value = {}, onChange}) => {
  const set = (from, to) => onChange(filled(from) || filled(to) ? {from, to} : undefined)
  const money = /₽/.test(filter.label)
  const props = money ? {formatter: thousands, parser: digits} : {}
  const name = nameOf(filter)
  return (
    <div className={`flat-filters__pill${filled(value.from) || filled(value.to) ? ' flat-filters__pill--active' : ''}`}>
      <span className='flat-filters__name'>{name}</span>
      <InputNumber {...props} bordered={false} min={0} placeholder='от' aria-label={`${name} от`} value={value.from} onChange={(v) => set(v, value.to)}/>
      <span className='flat-filters__sep'/>
      <InputNumber {...props} bordered={false} min={0} placeholder='до' aria-label={`${name} до`} value={value.to} onChange={(v) => set(value.from, v)}/>
    </div>
  )
}

const DateRange = ({filter, value, onChange}) => {
  const setDate = (part, v) => {
    const next = {...(value || {}), [part]: v || undefined}
    onChange(filled(next.from) || filled(next.to) ? next : undefined)
  }
  return (
    <div className={`flat-filters__pill${isSet('dateRange', value) ? ' flat-filters__pill--active' : ''}`}>
      <span className='flat-filters__name'>{filter.label}</span>
      <Input bordered={false} type='date' aria-label={`${filter.label} с`} value={(value && value.from) || ''} onChange={(e) => setDate('from', e.target.value)}/>
      <span className='flat-filters__sep'/>
      <Input bordered={false} type='date' aria-label={`${filter.label} по`} value={(value && value.to) || ''} onChange={(e) => setDate('to', e.target.value)}/>
    </div>
  )
}

// one choice from a short list, the name inside the field («Тип договора  Любой»)
const Choice = ({filter, value, onChange, options}) => (
  <div className={`flat-filters__pill${filled(value) ? ' flat-filters__pill--active' : ''}`}>
    <span className='flat-filters__name'>{nameOf(filter)}</span>
    <Select bordered={false} value={value === undefined ? 'any' : value} onChange={(v) => onChange(v === 'any' ? undefined : v)}
      dropdownMatchSelectWidth={false} options={[{value: 'any', label: filter.kind === 'bool3' ? 'Не важно' : 'Любое'}, ...options]}/>
  </div>
)

// a list filter's options with tick boxes, so it shows that several can be chosen (owner 02.11.2026); the field and the
// search go by the option's name
const ticked = (options, chosen) => options.map((o) => (o.options
  ? {...o, options: ticked(o.options, chosen)}
  : {...o, name: o.label, label: <Checkbox className='flat-filters__tick' checked={chosen.includes(o.value)}>{o.label}</Checkbox>}))

// a list filter: several values by their tick boxes, nothing chosen = undefined; the list as wide as its longest name, so
// no option is cut; what does not fit in the field is named, not «+ 1 ...»
export const TickedSelect = ({options, value, onChange, className = '', ...props}) => (
  <Select mode='multiple' allowClear showSearch optionFilterProp='name' optionLabelProp='name' menuItemSelectedIcon={null}
    maxTagCount='responsive' dropdownMatchSelectWidth={false}
    maxTagPlaceholder={(omitted) => {
      const names = omitted.map((o) => String(o.label))
      return <span title={names.join(', ')}>{names.length === 1 ? names[0] : `Выбрано: ${names.length}`}</span>
    }}
    {...props}
    className={`flat-filters__select${value && value.length ? ' flat-filters__select--active' : ''} ${className}`.trim()}
    value={value || []} options={ticked(options, value || [])}
    onChange={(list) => onChange(list.length ? list : undefined)}/>
)

// one field of the panel; compact: the name is the placeholder (the portal's compact rows)
const Field = ({filter, value, onChange, facets, lists, compact}) => {
  const {key, kind} = filter
  const name = nameOf(filter)
  if (LIST_KINDS.includes(kind)) {
    // an option filter with nothing in the data and nothing chosen is left out, as on the portal
    const options = optionsOf(filter, facets, lists)
    if (filter.facet && !options.length && !isSet(kind, value)) return null
    return (
      <TickedSelect placeholder={compact ? name : `${name}: любые`} aria-label={name} value={value} options={options} onChange={onChange}/>
    )
  }
  if (kind === 'range') return <Range filter={filter} value={value} onChange={onChange}/>
  if (kind === 'dateRange') return <DateRange filter={filter} value={value} onChange={onChange}/>
  if (kind === 'bool3') {
    return <Choice filter={filter} value={value} onChange={onChange} options={[{value: true, label: 'Да'}, {value: false, label: 'Нет'}]}/>
  }
  if (kind === 'choice') {
    const options = CHOICES[key]
      ? CHOICES[key].map(([v, label]) => ({value: v, label}))
      : (filter.values || []).map((v) => ({value: v, label: choiceLabel(key, v)}))
    return <Choice filter={filter} value={value} onChange={onChange} options={options}/>
  }
  if (kind === 'flag') return null
  // «Этаж»: «3-7» or «2, 5, 9», as on ECI old; the other short texts
  return (
    <div className={`flat-filters__pill${filled(value) ? ' flat-filters__pill--active' : ''}`}>
      <span className='flat-filters__name'>{name}</span>
      <Input bordered={false} allowClear placeholder={PLACEHOLDERS[key]} title={filter.hint} aria-label={name} value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}/>
    </div>
  )
}

// lists and choices first, then from–to fields, then the short text ones (the portal's order)
const orderOf = (f) => (['text', 'custom', 'list'].includes(f.kind) ? 2 : RANGE_KINDS.includes(f.kind) ? 1 : 0)
const sorted = (filters) => filters.map((f, i) => ({f, i})).sort((a, b) => orderOf(a.f) - orderOf(b.f) || a.i - b.i).map(({f}) => f)

// The panel above the table
export const FiltersPanel = ({config, facets, lists, values, onChange, onReset, search, onSearch, onAll, total}) => {
  const filters = ((config && config.filters) || []).filter((f) => !SKIP.includes(f.key))
  const byKey = Object.fromEntries(filters.map((f) => [f.key, f]))
  const anySet = countSet(values, config) > 0
  return (
    <section className='flat-filters' aria-label='Фильтры'>
      <div className='flat-filters__grid'>
        <Input.Search allowClear className='flat-filters__search' placeholder='ID, номер квартиры, ЖК или адрес' value={search}
          onChange={(e) => onSearch(e.target.value)}/>
        {COMPACT.map((key) => byKey[key] && (
          <Field key={key} filter={byKey[key]} value={values[key]} onChange={(v) => onChange(key, v)} facets={facets} lists={lists} compact/>
        ))}
      </div>
      <div className='flat-filters__bar'>
        <div className='flat-filters__side'>
          {anySet && <Button type='link' onClick={onReset}>Сбросить фильтры</Button>}
        </div>
        <div className='flat-filters__center'>
          <span className='flat-filters__total'>Найдено {thousands(total)}</span>
        </div>
        <div className='flat-filters__side flat-filters__side--right'>
          {panelFilters(config).length > 0 && (
            <Badge count={countSet(values, config, 'panel')} size='small'>
              <Button icon={<FilterOutlined/>} onClick={onAll}>Все фильтры</Button>
            </Badge>
          )}
        </div>
      </div>
    </section>
  )
}

// the portal's names of its groups, but its «Для сотрудников»: in the admin every filter is for them
const GROUP_NAMES = {staff: 'Остальное'}

// «Все фильтры»: every filter of the compact rows' rest, by the portal's groups, on the side
export const AllFiltersDrawer = ({open, onClose, config, facets, lists, values, onChange, total}) => {
  const filters = panelFilters(config)
  const groups = ((config && config.groups) || []).filter((g) => filters.some((f) => f.group === g.key))
  return (
    <Drawer
      title='Все фильтры'
      placement='right'
      width={Math.min(520, window.innerWidth)}
      visible={open}
      onClose={onClose}
      footer={(
        <div className='apartment-filters__foot'>
          <Button onClick={() => filters.forEach((f) => isSet(f.kind, values[f.key]) && onChange(f.key, undefined))}>Сбросить</Button>
          <Button type='primary' onClick={onClose}>Показать {thousands(total)}</Button>
        </div>
      )}
    >
      {groups.map((group) => {
        const own = filters.filter((f) => f.group === group.key)
        const flags = own.filter((f) => f.kind === 'flag')
        return (
          <section key={group.key} className='apartment-filters__group'>
            <h3>{GROUP_NAMES[group.key] || group.label}</h3>
            {sorted(own.filter((f) => f.kind !== 'flag')).map((f) => (
              <div key={f.key} className='apartment-filters__field'>
                <Field filter={f} value={values[f.key]} onChange={(v) => onChange(f.key, v)} facets={facets} lists={lists}/>
              </div>
            ))}
            {flags.length > 0 && (
              <div className='apartment-filters__field'>
                {flags.map((f) => (
                  <Checkbox key={f.key} checked={Boolean(values[f.key])} onChange={(e) => onChange(f.key, e.target.checked || undefined)}>{f.label}</Checkbox>
                ))}
              </div>
            )}
          </section>
        )
      })}
    </Drawer>
  )
}
